import React, { useState } from "react";
import DetachedModal from "../../../../Shared/Modals/DetachedModal/DetachedModal";
import { Textarea } from "../../../../ui/textarea";
import { Input } from "../../../../ui/input";
import { Button } from "../../../../Shared/Buttons/Button/Button";
import { EDSButton } from "../../../../eds/EDSButton";
import { Deposit, BookingV4 } from "../../../../../types/bookings";
import {
  parseDepositAmount,
  formatCurrency,
  getCurrencySymbol
} from "../../../../../utils/number";

type RefundModalProps = {
  deposit: Deposit;
  booking: BookingV4;
  onClose: () => void;
  onSubmit: (refundData: RefundFormData) => Promise<void>;
};

export type RefundFormData = {
  amount: number;
  notes?: string;
};

const RefundModal = ({
  deposit,
  booking,
  onClose,
  onSubmit
}: RefundModalProps) => {
  const [formData, setFormData] = useState<RefundFormData>({
    amount: 0,
    notes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const currency = deposit.currency || "GBP";
  const depositAmount = parseDepositAmount(deposit.amount);

  const totalClaimed =
    deposit.claims?.reduce(
      (sum, claim) => sum + parseDepositAmount(claim.amount),
      0
    ) || 0;

  const refundRefs = deposit.refunds || deposit.refund_ids || [];
  const totalRefunded = refundRefs.reduce((sum, refundRef) => {
    const refund = booking.refunds?.find(
      (r) => r.id === refundRef || r.refund_ref === refundRef
    );
    if (refund) {
      return sum + parseDepositAmount(refund.amount);
    }
    return sum;
  }, 0);

  const maxRefundAmount = totalClaimed - totalRefunded;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = "Refund amount must be greater than 0";
    }

    if (formData.amount > maxRefundAmount) {
      newErrors.amount = `Refund amount cannot exceed ${formatCurrency(maxRefundAmount, currency)}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error("Error processing refund:", error);
      setErrors({ submit: "Failed to process refund. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof RefundFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleMaxRefund = () => {
    handleInputChange("amount", maxRefundAmount);
  };

  return (
    <DetachedModal heading="Process Refund" width="500px" handleClose={onClose}>
      <div className="RefundModal flex flex-col h-full gap-6">
        <div className="RefundModal-Info rounded-md p-4 border border-[var(--borderColor)]">
          <div className="RefundModal-InfoRow flex justify-between items-center mb-2 gap-4">
            <span className="RefundModal-Label font-medium text-[color:var(--textColorSecondary)] text-sm">
              Original Deposit:
            </span>
            <span className="RefundModal-Value font-semibold text-[color:var(--textColor)] text-sm break-all">
              {formatCurrency(depositAmount, currency)}
            </span>
          </div>
          <div className="RefundModal-InfoRow flex justify-between items-center mb-2 gap-4">
            <span className="RefundModal-Label font-medium text-[color:var(--textColorSecondary)] text-sm">
              Already Claimed:
            </span>
            <span className="RefundModal-Value font-semibold text-[color:var(--textColor)] text-sm break-all">
              {formatCurrency(totalClaimed, currency)}
            </span>
          </div>
          <div className="RefundModal-InfoRow flex justify-between items-center mb-2 gap-4">
            <span className="RefundModal-Label font-medium text-[color:var(--textColorSecondary)] text-sm">
              Already Refunded:
            </span>
            <span className="RefundModal-Value font-semibold text-[color:var(--textColor)] text-sm break-all">
              {formatCurrency(totalRefunded, currency)}
            </span>
          </div>
          <div className="RefundModal-InfoRow flex justify-between items-center mb-2 gap-4">
            <span className="RefundModal-Label font-medium text-[color:var(--textColorSecondary)] text-sm">
              Available to Refund:
            </span>
            <span className="RefundModal-Value font-semibold text-[color:var(--textColor)] text-sm break-all">
              {formatCurrency(maxRefundAmount, currency)}
            </span>
          </div>
          <div className="RefundModal-InfoRow flex justify-between items-center mb-2 gap-4">
            <span className="RefundModal-Label font-medium text-[color:var(--textColorSecondary)] text-sm">
              Deposit Date:
            </span>
            <span className="RefundModal-Value font-semibold text-[color:var(--textColor)] text-sm break-all">
              {new Date(deposit.date).toLocaleDateString(undefined, {
                dateStyle: "medium"
              })}
            </span>
          </div>
          {deposit.payment_ref && (
            <div className="RefundModal-InfoRow RefundModal-InfoRow--PaymentRef flex justify-between items-center mb-0 gap-0">
              <span className="RefundModal-Label font-medium text-[color:var(--textColorSecondary)] text-sm basis-3/5 shrink-0">
                Payment Reference:
              </span>
              <span className="RefundModal-Value font-semibold text-[color:var(--textColor)] text-sm break-all basis-2/5 shrink-0">
                {deposit.payment_ref}
              </span>
            </div>
          )}
        </div>

        <div className="RefundModal-Form flex flex-col gap-4">
          <div className="RefundModal-Field flex flex-col gap-2">
            <label
              htmlFor="refund-amount"
              className="font-medium text-[color:var(--textColor)] text-sm"
            >
              Refund Amount *
            </label>
            <div className="RefundModal-AmountContainer flex items-center gap-2 relative">
              <span className="RefundModal-CurrencyPrefix font-medium text-[color:var(--textColor)] text-base shrink-0">
                {getCurrencySymbol(currency)}
              </span>
              <Input
                id="refund-amount"
                type="number"
                value={formData.amount.toString()}
                onChange={(e) =>
                  handleInputChange("amount", parseFloat(e.target.value) || 0)
                }
                placeholder="0.00"
                step="0.01"
                min="0"
                max={maxRefundAmount}
                maxLength={15}
              />
              <EDSButton
                onClick={handleMaxRefund}
                disabled={isSubmitting}
                variant="outline"
                size="sm"
              >
                Max
              </EDSButton>
            </div>
            {errors.amount && (
              <span className="RefundModal-Error text-[color:var(--errorColor,#d32f2f)] text-xs mt-1">
                {errors.amount}
              </span>
            )}
          </div>

          <div className="RefundModal-Field flex flex-col gap-2">
            <label
              htmlFor="refund-notes"
              className="font-medium text-[color:var(--textColor)] text-sm"
            >
              Refund Notes
            </label>
            <Textarea
              id="refund-notes"
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder="Add notes about this refund..."
              rows={3}
            />
          </div>

          {errors.submit && (
            <div className="RefundModal-Error RefundModal-SubmitError bg-[var(--errorBackgroundColor,#ffebee)] border border-[var(--errorColor,#d32f2f)] rounded p-3 mt-2 text-[color:var(--errorColor,#d32f2f)] text-xs">
              {errors.submit}
            </div>
          )}
        </div>

        <div className="RefundModal-Actions mt-auto pt-4 shrink-0 flex gap-3 justify-end border-t border-[var(--borderColor)]">
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            fill="outline"
            size="md"
            type="default"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            fill="solid"
            size="md"
            type="primary"
          >
            {isSubmitting ? "Processing..." : "Process Refund"}
          </Button>
        </div>
      </div>
    </DetachedModal>
  );
};

export default RefundModal;
